import { ReportTypeID } from '~/src/dash/apiV2/reporting/reportTypes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { intEnumToArray } from '~/src/dash/utils/utils_enum';

export interface IDeviceType {
  key: string;
  text: string;
}

// NOTE: The key directly applies to the SQL builder, don't change unless necessary
export const deviceTypes: IDeviceType[] = [
  {
    key: 'PERSONAL_COMPUTER',
    text: 'Desktop',
  },
  {
    key: 'CONNECTED_TV',
    text: 'Connected TV',
  },
  {
    key: 'HIGHEND_PHONE',
    text: 'Mobile',
  },
  {
    key: 'TABLET',
    text: 'Tablet',
  },
  {
    key: 'CONNECTED_DEVICE',
    text: 'Connected Device',
  },
  {
    key: 'SET_TOP_BOX',
    text: 'Set-Top Box',
  },
];

export const allDeviceTypes: IDeviceType[] = [
  {
    key: 'DEVICETYPE_UNKNOWN',
    text: 'Unknown Device Type',
  },
  {
    key: 'PERSONAL_COMPUTER',
    text: 'Desktop',
  },
  {
    key: 'CONNECTED_TV',
    text: 'Connected TV',
  },
  {
    key: 'HIGHEND_PHONE',
    text: 'Mobile',
  },
  {
    key: 'TABLET',
    text: 'Tablet',
  },
  {
    key: 'CONNECTED_DEVICE',
    text: 'Connected Device',
  },
  {
    key: 'SET_TOP_BOX',
    text: 'Set-Top Box',
  },
];

const IASFirstPartyDeviceTypes: Array<{ key: string; text: string }> = [
  {
    key: '111|112',
    text: 'Desktop Web',
  },
  {
    key: '121|122',
    text: 'Mobile Web',
  },
  {
    key: '131|132',
    text: 'Tablet Web',
  },
  {
    key: '221|222',
    text: 'Mobile App',
  },
  {
    key: '231|232',
    text: 'Tablet App',
  },
  {
    key: '252',
    text: 'CTV App',
  },
];

const IASThirdPartyDeviceTypes: Array<{ key: string; text: string }> = [
  {
    key: '111|112',
    text: 'Desktop Web',
  },
  {
    key: '121|122',
    text: 'Mobile Web',
  },
  {
    key: '131|132',
    text: 'Tablet Web',
  },
  {
    key: '221|222',
    text: 'Mobile App',
  },
  {
    key: '231|232',
    text: 'Tablet App',
  },
  {
    key: '252',
    text: 'CTV App',
  },
  {
    key: '152',
    text: 'CTV Web',
  },
];

export function createAllDeviceTypesMenuList(): IMenuList {
  const out = {} as IMenuList;
  allDeviceTypes.forEach((dT) => {
    out[dT.key] = {
      ID: dT.key,
      selected: false,
      title: dT.text,
    };
  });
  return out;
}

export function createReportingDeviceTypesMenuList(reportTypeID: ReportTypeID): IMenuList {
  let deviceTypes;
  switch (reportTypeID) {
    case ReportTypeID.IASFirstPartyVASTTrackingReport:
      deviceTypes = IASFirstPartyDeviceTypes;
      break;

    case ReportTypeID.IASThirdPartyVASTTrackingReport:
      deviceTypes = IASThirdPartyDeviceTypes;
      break;

    default:
      deviceTypes = allDeviceTypes;
  }

  return deviceTypes.reduce((acc, deviceType) => {
    acc[deviceType.key] = {
      ID: deviceType.key,
      selected: false,
      title: deviceType.text,
    };
    return acc;
  }, {} as IMenuList);
}

// Used for Bidder and Campaign targeting
export const deviceORTBTypes: IDeviceType[] = [
  {
    key: '0',
    text: 'Unknown Device Type',
  },
  {
    key: '2',
    text: 'Desktop',
  },
  {
    key: '3',
    text: 'Connected TV',
  },
  {
    key: '4',
    text: 'Mobile',
  },
  {
    key: '5',
    text: 'Tablet',
  },
  {
    key: '6',
    text: 'Connected Device',
  },
  {
    key: '7',
    text: 'Set-Top Box',
  },
];

export function getDeviceTypesTextFromKey(key: string): string {
  return (deviceORTBTypes.find((deviceType: IDeviceType) => deviceType.key === key) || { text: '' })
    .text;
}

export enum EDeviceTypes {
  DEVICETYPE_UNKNOWN = 0,
  MOBILE = 1,
  PERSONAL_COMPUTER = 2,
  CONNECTED_TV = 3,
  HIGHEND_PHONE = 4,
  TABLET = 5,
  CONNECTED_DEVICE = 6,
  SET_TOP_BOX = 7,
}

export const EDeviceTypesName: Record<string, string> = {
  DEVICETYPE_UNKNOWN: 'DEVICETYPE_UNKNOWN',
  MOBILE: 'MOBILE',
  PERSONAL_COMPUTER: 'PERSONAL_COMPUTER',
  CONNECTED_TV: 'CONNECTED_TV',
  HIGHEND_PHONE: 'HIGHEND_PHONE',
  TABLET: 'TABLET',
  CONNECTED_DEVICE: 'CONNECTED_DEVICE',
  SET_TOP_BOX: 'SET_TOP_BOX',
};

export const EDeviceTypesValues = intEnumToArray(EDeviceTypes);

export function EDeviceTypesToString(e: EDeviceTypes): string {
  switch (e) {
    case EDeviceTypes.DEVICETYPE_UNKNOWN:
      return 'Unknown Device Type';
    case EDeviceTypes.MOBILE:
      return 'Mobile';
    case EDeviceTypes.PERSONAL_COMPUTER:
      return 'Desktop';
    case EDeviceTypes.CONNECTED_TV:
      return 'Connected TV';
    case EDeviceTypes.HIGHEND_PHONE:
      return 'Highend Phone';
    case EDeviceTypes.TABLET:
      return 'Tablet';
    case EDeviceTypes.CONNECTED_DEVICE:
      return 'Connected Device';
    case EDeviceTypes.SET_TOP_BOX:
      return 'Set-Top Box';
  }
  return 'Unknown Device Type';
}

export function generateDeviceTypeMenuList(): IMenuList {
  const deviceTypeList = {} as IMenuList;
  for (const d of deviceORTBTypes) {
    deviceTypeList[d.key] = {
      selected: false,
      ID: d.key,
      title: d.text,
    } as IListItem;
  }

  return deviceTypeList;
}

export function genDeviceTypeMenuListWithSelectedAndDisable(
  deviceIDs: string,
  disabledIDs?: string[],
): IMenuList {
  const deviceTypeList = generateDeviceTypeMenuList();

  for (const selected of deviceIDs.split(',')) {
    if (deviceTypeList[selected]) {
      deviceTypeList[selected].selected = true;
    }
  }

  if ((disabledIDs || []).length) {
    for (const disabled of disabledIDs || []) {
      if (deviceTypeList[disabled]) {
        deviceTypeList[disabled].disabled = true;
      }
    }
  }

  return deviceTypeList;
}

export const allDeviceTypeEnums: IDeviceType[] = [
  {
    key: EDeviceTypes.DEVICETYPE_UNKNOWN.toString(),
    text: 'Unknown Device Type',
  },
  {
    key: EDeviceTypes.PERSONAL_COMPUTER.toString(),
    text: 'Desktop',
  },
  {
    key: EDeviceTypes.CONNECTED_TV.toString(),
    text: 'Connected TV',
  },
  {
    key: EDeviceTypes.MOBILE.toString(),
    text: 'Mobile',
  },
  {
    key: EDeviceTypes.TABLET.toString(),
    text: 'Tablet',
  },
  {
    key: EDeviceTypes.CONNECTED_DEVICE.toString(),
    text: 'Connected Device',
  },
  {
    key: EDeviceTypes.SET_TOP_BOX.toString(),
    text: 'Set-Top Box',
  },
];

export function createAllDeviceTypesAsEnumMenuList(): IMenuList {
  const out = {} as IMenuList;
  allDeviceTypeEnums.forEach((dT) => {
    out[dT.key] = {
      ID: dT.key,
      selected: false,
      title: dT.text,
    };
  });
  return out;
}
